import type { Circuit, PlayerTrackPosition } from '../types/track.js';
import { TrackCellType } from '../types/track.js';
import { getPlayerTrackCell } from './jokerLap.js';
import { buildRaceRankingEntries, sortPlayerIndicesForRanking } from './raceRanking.js';
import { trackPositionGridKey } from './trackMovement.js';
import { trackMovementOccupancyKey } from './trackOccupancyKey.js';
import { computeTrackProgressScore } from './trackProgress.js';
import { isStartSideOnSfOverlap } from './circuitLoop.js';
import type { SameCellOvertakePair } from './turnOrder.js';

export interface RaceDebugContext {
  source: 'server' | 'client-solo' | 'client-mp';
  circuit: Circuit;
  numPlayers: number;
  numLaps: number;
  /** Compteur monotone d’état (utile pour diagnostiquer les désynchronisations). */
  getStateSeq?: () => number;
  turnOrder: readonly number[];
  turnSlot: number;
  currentPlayerIndex: number;
  sameCellOvertakePairs: readonly SameCellOvertakePair[];
  isOpeningRound: boolean;
  getPlayerTrackPosition: (playerIndex: number) => PlayerTrackPosition;
  getLapCount: (playerIndex: number) => number;
  getArrivalStamp: (playerIndex: number) => number;
  getFinishStamp: (playerIndex: number) => number;
  getTotalDiceRolls: (playerIndex: number) => number;
  getSkipStartLineCrossingsRemaining: (playerIndex: number) => number;
  getHadStartLineSkipAtStart: (playerIndex: number) => boolean;
  getFinished: (playerIndex: number) => boolean;
  getName?: (playerIndex: number) => string;
}

export interface RaceDebugPlayerRow {
  i: number;
  name: string;
  pathIdx: number;
  jokerBranch: number | null;
  startLane: number | null;
  grid: string | null;
  cell: string | null;
  laps: number;
  skipSf: number;
  hadSfSkip: boolean;
  progress: number;
  arrival: number;
  finish: number;
  rolls: number;
  finished: boolean;
}

export interface RaceDebugSnapshot {
  ts: string;
  source: RaceDebugContext['source'];
  event: string;
  seq: number;
  stateSeq?: number;
  diceRound: number;
  turnOrder: number[];
  turnSlot: number;
  current: number;
  numLaps: number;
  openingRound: boolean;
  sameCellPairs: SameCellOvertakePair[];
  ranking: { i: number; rank: number; progress: number; laps: number; rolls: number }[];
  players: RaceDebugPlayerRow[];
  /** Ordre actifs par progressScore (piste), tie-break arrival puis index. */
  trackOrder?: number[];
  /** false si ranking actifs ≠ trackOrder. */
  rankVsTrackOk?: boolean;
  extra?: Record<string, unknown>;
}

export interface RankVsTrackMismatch {
  rankOrder: number[];
  trackOrder: number[];
  details: {
    i: number;
    rank: number;
    progress: number;
    laps: number;
    pathIdx: number;
    skipSf: number;
  }[];
}

/**
 * Compare le classement affiché à l’ordre piste (progressScore des joueurs actifs).
 * Retourne le détail de l’écart, ou null si cohérent.
 */
export function detectRankVsTrackMismatch(
  ranking: RaceDebugSnapshot['ranking'],
  players: readonly RaceDebugPlayerRow[]
): RankVsTrackMismatch | null {
  const byPlayer = new Map(players.map((p) => [p.i, p]));
  const activeRank = ranking.filter((r) => !(byPlayer.get(r.i)?.finished ?? false));
  if (activeRank.length < 2) return null;

  const rankOrder = activeRank.map((r) => r.i);
  const trackOrder = [...activeRank]
    .map((r) => {
      const p = byPlayer.get(r.i);
      return {
        i: r.i,
        progress: p?.progress ?? r.progress,
        arrival: p?.arrival ?? 0,
      };
    })
    .sort((a, b) => {
      if (b.progress !== a.progress) return b.progress - a.progress;
      if (a.arrival !== b.arrival) return a.arrival - b.arrival;
      return a.i - b.i;
    })
    .map((x) => x.i);

  if (JSON.stringify(rankOrder) === JSON.stringify(trackOrder)) return null;

  return {
    rankOrder,
    trackOrder,
    details: activeRank.map((r) => {
      const p = byPlayer.get(r.i);
      return {
        i: r.i,
        rank: r.rank,
        progress: p?.progress ?? r.progress,
        laps: p?.laps ?? r.laps,
        pathIdx: p?.pathIdx ?? 0,
        skipSf: p?.skipSf ?? 0,
      };
    }),
  };
}

type RaceDebugSink = (snapshot: RaceDebugSnapshot) => void;

const SINK_GLOBAL_KEY = '__multirxRaceDebugSink';

let sink: RaceDebugSink | null = null;
let seq = 0;

function readGlobalSink(): RaceDebugSink | null {
  if (typeof globalThis === 'undefined') return null;
  const g = globalThis as Record<string, unknown>;
  const candidate = g[SINK_GLOBAL_KEY];
  return typeof candidate === 'function' ? (candidate as RaceDebugSink) : null;
}

function writeGlobalSink(next: RaceDebugSink | null): void {
  if (typeof globalThis === 'undefined') return;
  const g = globalThis as Record<string, unknown>;
  if (next == null) {
    delete g[SINK_GLOBAL_KEY];
  } else {
    g[SINK_GLOBAL_KEY] = next;
  }
}

function activeSink(): RaceDebugSink | null {
  return sink ?? readGlobalSink();
}

export function setRaceDebugSink(next: RaceDebugSink | null): void {
  sink = next;
  writeGlobalSink(next);
  seq = 0;
}

export function isRaceDebugEnabled(): boolean {
  return activeSink() != null;
}

export function posLabel(circuit: Circuit, pos: PlayerTrackPosition): string {
  const cell = getPlayerTrackCell(circuit, pos);
  const grid = trackPositionGridKey(circuit, pos);
  const occ = trackMovementOccupancyKey(circuit, pos);
  const type = cell?.type ?? '?';
  return `path=${pos.mainPathIndex} grid=${grid ?? '?'} occ=${occ ?? '?'} ${type}`;
}

function buildPlayerRows(ctx: RaceDebugContext): RaceDebugPlayerRow[] {
  const { circuit, numPlayers } = ctx;
  const indices = Array.from({ length: numPlayers }, (_, i) => i);
  const rows: RaceDebugPlayerRow[] = [];
  for (let i = 0; i < numPlayers; i += 1) {
    const pos = ctx.getPlayerTrackPosition(i);
    const laps = ctx.getLapCount(i);
    const rolls = ctx.getTotalDiceRolls(i);
    const skipSf = ctx.getSkipStartLineCrossingsRemaining(i);
    const hadSfSkip = ctx.getHadStartLineSkipAtStart(i);
    const cell = getPlayerTrackCell(circuit, pos);
    const startSideOnSfOverlap = isStartSideOnSfOverlap(
      circuit,
      i,
      pos,
      laps,
      (j) => ctx.getPlayerTrackPosition(j),
      (j) => ctx.getLapCount(j),
      indices
    );
    rows.push({
      i,
      name: ctx.getName?.(i) ?? `P${i}`,
      pathIdx: pos.mainPathIndex,
      jokerBranch: pos.jokerBranchIndex ?? null,
      startLane: pos.startLaneIndex ?? null,
      grid: trackPositionGridKey(circuit, pos),
      cell: cell ? TrackCellType[cell.type] : null,
      laps,
      skipSf,
      hadSfSkip,
      progress: computeTrackProgressScore(circuit, pos, laps, {
        totalDiceRolls: rolls,
        startSideOnSfOverlap,
        skipStartLineCrossingsRemaining: skipSf,
        hadStartLineSkipAtStart: hadSfSkip,
      }),
      arrival: ctx.getArrivalStamp(i),
      finish: ctx.getFinishStamp(i),
      rolls,
      finished: ctx.getFinished(i),
    });
  }
  return rows;
}

function buildRankingRows(ctx: RaceDebugContext): RaceDebugSnapshot['ranking'] {
  const entries = buildRaceRankingEntries(ctx.circuit, ctx.numPlayers, (i) => {
    const pos = ctx.getPlayerTrackPosition(i);
    return {
      finished: ctx.getFinished(i),
      finishStamp: ctx.getFinishStamp(i),
      lapCount: ctx.getLapCount(i),
      mainPathIndex: pos.mainPathIndex,
      jokerBranchIndex: pos.jokerBranchIndex,
      startLaneIndex: pos.startLaneIndex,
      arrivalStamp: ctx.getArrivalStamp(i),
      totalDiceRolls: ctx.getTotalDiceRolls(i),
      skipStartLineCrossingsRemaining: ctx.getSkipStartLineCrossingsRemaining(i),
      hadStartLineSkipAtStart: ctx.getHadStartLineSkipAtStart(i),
    };
  });
  const sorted = sortPlayerIndicesForRanking(entries, ctx.circuit);
  const byIndex = new Map(entries.map((e) => [e.playerIndex, e]));
  return sorted.map((playerIndex, rank) => {
    const e = byIndex.get(playerIndex);
    return {
      i: playerIndex,
      rank: rank + 1,
      progress: e?.progressScore ?? e?.pathIndex ?? 0,
      laps: e?.lapCount ?? 0,
      rolls: e?.totalDiceRolls ?? 0,
    };
  });
}

function diceRoundLabel(ctx: RaceDebugContext): number {
  let min = Number.POSITIVE_INFINITY;
  for (let i = 0; i < ctx.numPlayers; i += 1) {
    if (ctx.getFinished(i)) continue;
    min = Math.min(min, ctx.getTotalDiceRolls(i));
  }
  return Number.isFinite(min) ? min + 1 : 0;
}

export function logRaceDebugEvent(
  event: string,
  ctx: RaceDebugContext,
  extra?: Record<string, unknown>
): void {
  const deliver = activeSink();
  if (!deliver) return;
  seq += 1;
  try {
    const ranking = buildRankingRows(ctx);
    const players = buildPlayerRows(ctx);
    const mismatch = detectRankVsTrackMismatch(ranking, players);
    const trackOrder =
      mismatch?.trackOrder ??
      ranking.filter((r) => !(players.find((p) => p.i === r.i)?.finished ?? false)).map((r) => r.i);

    const snapshot: RaceDebugSnapshot = {
      ts: new Date().toISOString(),
      source: ctx.source,
      event,
      seq,
      stateSeq: ctx.getStateSeq?.(),
      diceRound: diceRoundLabel(ctx),
      turnOrder: [...ctx.turnOrder],
      turnSlot: ctx.turnSlot,
      current: ctx.currentPlayerIndex,
      numLaps: ctx.numLaps,
      openingRound: ctx.isOpeningRound,
      sameCellPairs: [...ctx.sameCellOvertakePairs],
      ranking,
      players,
      trackOrder,
      rankVsTrackOk: mismatch == null,
      extra: mismatch != null ? { ...extra, rankVsTrack: mismatch } : extra,
    };
    deliver(snapshot);

    // Événement dédié (une seule fois, pas de re-entrée).
    if (mismatch != null && event !== 'RANK_VS_TRACK') {
      seq += 1;
      deliver({
        ...snapshot,
        ts: new Date().toISOString(),
        event: 'RANK_VS_TRACK',
        seq,
        extra: {
          trigger: event,
          ...mismatch,
        },
      });
    }
  } catch (err) {
    // Ne jamais faire échouer la course pour un log — mais signaler en dev.
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      console.warn('[RACE-DBG] logRaceDebugEvent failed', err);
    }
  }
}

export function clearRaceDebugSequence(): void {
  seq = 0;
}
